import React, { useState, useEffect } from 'react';
import { BookOpen, Calendar, Clock, CheckCircle, AlertCircle, Loader, User, ChevronRight, FileText, ArrowLeft, Star, Award, TrendingUp, Target, ChevronDown } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import apiService from '../parent-dashboard/api/apiService';
import preparatoryApiService from '../parent-dashboard/api/preparatoryApiService';
import middleApiService from '../parent-dashboard/api/middleApiService';
import { detectGrade, getApiServiceForGrade, isMiddleStageGrade, isPreparatoryGrade } from '../parent-dashboard/utils/gradeUtils';
import AssignmentDetailView from '../parent-dashboard/views/AssignmentDetailView';
import Notification from '../parent-dashboard/components/Notification';

const LearnerActivities = () => {
    const [children, setChildren] = useState([]);
    const [selectedChild, setSelectedChild] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    // Assignment detail state
    const [currentView, setCurrentView] = useState('list'); // 'list' or 'detail'
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [detectedGrade, setDetectedGrade] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: "", type: "" });

    // Caregiver observation form state
    const [observationForm, setObservationForm] = useState({
        resources: [],
        other_resources: "",
        assessment: {
            finds_school_safe: "Not sure",
            participates_in_activities: "Not sure",
            finds_curriculum_difficult: "Not sure",
            making_good_progress: "Not sure",
            gets_school_support: "Not sure",
            can_talk_feelings: "Not sure",
            can_calm_down: "Not sure",
            can_understand_friends: "Not sure",
            respects_opinions: "Not sure",
            helps_after_fight: "Not sure",
            comforts_when_sad: "Not sure"
        },
        needs_support: [],
        other_support: "",
        understanding: {
            motivated_to_learn: "Not sure",
            follows_schedule: "Not sure",
            finds_curriculum_difficult: "Not sure",
            making_good_progress: "Not sure"
        },
        school_support_needs: [],
        other_school_support: "",
        home_support_plan: ""
    });

    useEffect(() => {
        fetchChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            // Detect grade first, then fetch assignments
            const grade = detectGrade(selectedChild);
            if (grade) {
                setDetectedGrade(grade);
                console.log(`Detected grade ${grade} for child ${selectedChild.name}, fetching assignments...`);
            }
            fetchChildAssignments(selectedChild.student_id || selectedChild.id, grade);
        }
    }, [selectedChild]);

    // Fetch assignment submission when an assignment is selected
    useEffect(() => {
        if (selectedAssignment && currentView === 'detail') {
            fetchSubmission(selectedChild.student_id || selectedChild.id, selectedAssignment.assignment_id || selectedAssignment.id);
        }
    }, [selectedAssignment, currentView]);

    const fetchChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            setError('Failed to fetch children');
            console.error(err);
        }
    };

    const fetchChildAssignments = async (childId, grade = null) => {
        setLoading(true);
        try {
            // Use the passed grade or the detected grade
            const gradeToUse = grade || detectedGrade;
            
            // Determine which API service to use based on grade
            const serviceToUse = getApiServiceForGrade(gradeToUse, selectedChild, null);
            const isPrep = isPreparatoryGrade(gradeToUse);
            const isMid = isMiddleStageGrade(gradeToUse);

            console.log(`Fetching assignments using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${gradeToUse}`);

            let assignmentsData;
            if (isMid && serviceToUse.getChildAssignments) {
                // Use middle stage API service
                assignmentsData = await serviceToUse.getChildAssignments(childId);
            } else if (isPrep && serviceToUse.getChildAssignments) {
                // Use preparatory stage API service
                assignmentsData = await serviceToUse.getChildAssignments(childId);
            } else {
                // Use foundational API service (ParentAPI)
                assignmentsData = await ParentAPI.getChildAssignments(childId);
            }

            setAssignments(assignmentsData);
        } catch (err) {
            setError('Failed to fetch assignments');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChildSelect = (child) => {
        setSelectedChild(child);
        // Detect grade when selecting a child
        const grade = detectGrade(child);
        if (grade) {
            setDetectedGrade(grade);
            console.log(`Selected child (${child.name}) with detected grade ${grade}`);
        }
    };

    // Handle assignment selection for detail view
    const handleAssignmentSelect = (assignment) => {
        setSelectedAssignment(assignment);
        setCurrentView('detail');
    };

    // Handle back navigation
    const handleBack = () => {
        setCurrentView('list');
        setSelectedAssignment(null);
        setSelectedSubmission(null);
        resetObservationForm();
    };

    // Reset observation form based on stage
    const resetObservationForm = () => {
        const isPrep = getIsPreparatory();
        const isMid = getIsMiddleStage();

        if (isMid) {
            setObservationForm({
                resources: [],
                other_resources: "",
                understanding: {
                    motivated_to_learn: "Not sure",
                    follows_schedule: "Not sure",
                    finds_curriculum_difficult: "Not sure",
                    making_good_progress: "Not sure"
                },
                school_support_needs: [],
                other_school_support: "",
                home_support_plan: ""
            });
        } else if (isPrep) {
            setObservationForm({
                resources: [],
                other_resources: "",
                assessment: {
                    finds_school_safe: "Not sure",
                    participates_in_activities: "Not sure",
                    finds_curriculum_difficult: "Not sure",
                    making_good_progress: "Not sure",
                    gets_school_support: "Not sure",
                    can_talk_feelings: "Not sure",
                    can_calm_down: "Not sure",
                    can_understand_friends: "Not sure",
                    respects_opinions: "Not sure",
                    helps_after_fight: "Not sure",
                    comforts_when_sad: "Not sure"
                },
                needs_support: [],
                other_support: ""
            });
        } else {
            setObservationForm({
                resources: [],
                comments: ""
            });
        }
    };

    // Determine if the current child is in preparatory stage
    const getIsPreparatory = () => {
        // Always re-detect grade to ensure we have the latest value
        const currentGrade = detectedGrade || detectGrade(selectedChild);
        return isPreparatoryGrade(currentGrade);
    };

    // Determine if the current child is in middle stage
    const getIsMiddleStage = () => {
        // Always re-detect grade to ensure we have the latest value
        const currentGrade = detectedGrade || detectGrade(selectedChild);
        return isMiddleStageGrade(currentGrade);
    };

    // Get the appropriate API service based on grade
    const getApiService = () => {
        // Always re-detect grade to ensure we have the latest value
        const currentGrade = detectedGrade || detectGrade(selectedChild);
        return getApiServiceForGrade(currentGrade, selectedChild, selectedAssignment);
    };

    // Fetch submission for an assignment
    const fetchSubmission = async (childId, assignmentId) => {
        setLoading(true);
        try {
            // Use the detected grade to determine API service
            const gradeToUse = detectedGrade;
            const serviceToUse = getApiServiceForGrade(gradeToUse, selectedChild, selectedAssignment);
            const isPrep = isPreparatoryGrade(gradeToUse);
            const isMid = isMiddleStageGrade(gradeToUse);

            console.log(`Fetching submission using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${gradeToUse}`);

            const submission = await serviceToUse.getChildSubmission(childId, assignmentId);
            console.log("Received submission:", submission);

            setSelectedSubmission(submission);

            // Pre-fill the observation form based on stage
            if (submission?.caregiver_observation) {
                if (isMid) {
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        other_resources: submission.caregiver_observation.other_resources || "",
                        understanding: submission.caregiver_observation.understanding || {
                            motivated_to_learn: "Not sure",
                            follows_schedule: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure"
                        },
                        school_support_needs: submission.caregiver_observation.school_support_needs || [],
                        other_school_support: submission.caregiver_observation.other_school_support || "",
                        home_support_plan: submission.caregiver_observation.home_support_plan || ""
                    });
                } else if (isPrep) {
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        other_resources: submission.caregiver_observation.other_resources || "",
                        assessment: submission.caregiver_observation.assessment || {
                            finds_school_safe: "Not sure",
                            participates_in_activities: "Not sure",
                            finds_curriculum_difficult: "Not sure",
                            making_good_progress: "Not sure",
                            gets_school_support: "Not sure",
                            can_talk_feelings: "Not sure",
                            can_calm_down: "Not sure",
                            can_understand_friends: "Not sure",
                            respects_opinions: "Not sure",
                            helps_after_fight: "Not sure",
                            comforts_when_sad: "Not sure"
                        },
                        needs_support: submission.caregiver_observation.needs_support || [],
                        other_support: submission.caregiver_observation.other_support || ""
                    });
                } else {
                    setObservationForm({
                        resources: submission.caregiver_observation.resources || [],
                        comments: submission.caregiver_observation.comments || ""
                    });
                }
            } else {
                resetObservationForm();
            }
        } catch (err) {
            console.error("Error fetching submission:", err);
            if (err.message && (err.message.includes("404") || err.message.toLowerCase().includes('not found'))) {
                setSelectedSubmission(null);
                console.log("No submission found for this assignment, which is expected.");
            } else {
                showNotification(`Failed to fetch submission: ${err.message}`, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle resource selection in observation form
    const handleResourceChange = (resource) => {
        setObservationForm(prev => {
            const resources = [...prev.resources];
            if (resources.includes(resource)) {
                return { ...prev, resources: resources.filter(r => r !== resource) };
            } else {
                return { ...prev, resources: [...resources, resource] };
            }
        });
    };

    // Handle assessment changes in observation form
    const handleAssessmentChange = (field, value) => {
        setObservationForm(prev => ({
            ...prev,
            assessment: {
                ...prev.assessment,
                [field]: value
            }
        }));
    };

    // Handle understanding changes for middle stage
    const handleUnderstandingChange = (field, value) => {
        setObservationForm(prev => ({
            ...prev,
            understanding: {
                ...prev.understanding,
                [field]: value
            }
        }));
    };

    // Handle support needs changes in observation form
    const handleSupportChange = (area) => {
        const isMid = getIsMiddleStage();
        const fieldName = isMid ? 'school_support_needs' : 'needs_support';

        setObservationForm(prev => {
            const needs = [...prev[fieldName]];
            if (needs.includes(area)) {
                return { ...prev, [fieldName]: needs.filter(n => n !== area) };
            } else {
                return { ...prev, [fieldName]: [...needs, area] };
            }
        });
    };

    // Handle text input changes in observation form
    const handleCommentsChange = (e) => {
        const { name, value } = e.target;
        setObservationForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle observation form submission
    const handleObservationSubmit = async (e) => {
        e.preventDefault();

        if (observationForm.resources.length === 0) {
            showNotification("Please select at least one resource.", "error");
            return;
        }

        setLoading(true);
        try {
            // Use the detected grade to determine API service
            const gradeToUse = detectedGrade;
            const serviceToUse = getApiServiceForGrade(gradeToUse, selectedChild, selectedAssignment);
            const isPrep = isPreparatoryGrade(gradeToUse);
            const isMid = isMiddleStageGrade(gradeToUse);

            console.log(`Submitting observation using ${isMid ? 'middle' : isPrep ? 'preparatory' : 'foundation'} API service for grade ${gradeToUse}`);

            const result = await serviceToUse.addCaregiverObservation(
                selectedChild.student_id || selectedChild.id,
                selectedAssignment.assignment_id || selectedAssignment.id,
                observationForm
            );

            setSelectedSubmission(prev => ({
                ...prev,
                caregiver_observation: result.caregiver_observation || result
            }));

            showNotification("Caregiver observation submitted successfully!", "success");
        } catch (err) {
            showNotification(`Failed to submit observation: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Show notification
    const showNotification = (message, type = "success") => {
        setNotification({
            show: true,
            message,
            type
        });

        setTimeout(() => {
            setNotification({ show: false, message: "", type: "" });
        }, 3000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const getStatusIcon = (assignment) => {
        if (assignment.completed) {
            return <CheckCircle className="h-5 w-5 text-green-500" />;
        }
        if (assignment.due_date && new Date(assignment.due_date) < new Date()) {
            return <AlertCircle className="h-5 w-5 text-red-500" />;
        }
        return <Clock className="h-5 w-5 text-yellow-500" />;
    };

    const getStatusText = (assignment) => {
        if (assignment.completed) return 'Completed';
        if (assignment.due_date && new Date(assignment.due_date) < new Date()) return 'Overdue';
        return 'Pending';
    };

    const getStatusColor = (assignment) => {
        if (assignment.completed) return 'text-green-600 bg-green-50';
        if (assignment.due_date && new Date(assignment.due_date) < new Date()) return 'text-red-600 bg-red-50';
        return 'text-yellow-600 bg-yellow-50';
    };

    const getFilteredAssignments = () => {
        switch (activeTab) {
            case 'completed':
                return assignments.filter(a => a.completed);
            case 'pending':
                return assignments.filter(a => !a.completed && (!a.due_date || new Date(a.due_date) >= new Date()));
            case 'overdue':
                return assignments.filter(a => !a.completed && a.due_date && new Date(a.due_date) < new Date());
            default:
                return assignments;
        }
    };

    // Show assignment detail view
    if (currentView === 'detail' && selectedChild && selectedAssignment) {
        const isPreparatory = isPreparatoryGrade(detectedGrade);
        const isMiddleStage = isMiddleStageGrade(detectedGrade);

        return (
            <Layout pageTitle={`Activity: ${selectedAssignment.title || "Details"}`}>
                <AssignmentDetailView
                    selectedChild={{
                        ...selectedChild,
                        grade: detectedGrade
                    }}
                    assignments={assignments}
                    selectedAssignment={selectedAssignment}
                    selectedSubmission={selectedSubmission}
                    detectedGrade={detectedGrade}
                    handleBack={handleBack}

                    // Caregiver observation form props
                    observationForm={observationForm}
                    setObservationForm={setObservationForm}
                    handleResourceChange={handleResourceChange}
                    handleCommentsChange={handleCommentsChange}
                    handleObservationSubmit={handleObservationSubmit}

                    // Additional handlers for preparatory and middle stages
                    handleAssessmentChange={handleAssessmentChange}
                    handleSupportChange={handleSupportChange}
                    handleUnderstandingChange={handleUnderstandingChange}

                    // Navigation and UI state
                    loading={loading}
                    error={error}

                    // Pass stage flags
                    isPreparatory={isPreparatory}
                    isMiddleStage={isMiddleStage}

                    // Pass stage-specific API services
                    apiService={apiService}
                    preparatoryApiService={preparatoryApiService}
                    middleApiService={middleApiService}

                    // Call this when new caregiver observations are submitted
                    onCaregiverObservationComplete={() => {
                        fetchSubmission(
                            selectedChild.student_id || selectedChild.id,
                            selectedAssignment.assignment_id || selectedAssignment.id
                        );
                    }}
                />
                <Notification
                    notification={notification}
                    setNotification={setNotification}
                />
            </Layout>
        );
    }

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Activities">
                <div className="min-h-screen font-[poppins] flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <Loader className="h-12 w-12 animate-spin text-[#F17C45] mx-auto mb-4" />
                        <p className="text-gray-600">Loading activities...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout pageTitle="Learner's Activities">
                <div className="min-h-screen font-[poppins] flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Activities</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#F17C45] hover:bg-[#E16A3A]"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Activities">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    border: 1px solid #e5e7eb;
                    background: white;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .card-parent:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                
                .activity-card {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    transition: box-shadow 0.2s ease;
                }
                
                .activity-card:hover {
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .status-completed {
                    background: #059669;
                    color: white;
                }
                
                .status-pending {
                    background: #f59e0b;
                    color: white;
                }
                
                .status-overdue {
                    background: #dc2626;
                    color: white;
                }
                
                .child-selector {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    transition: all 0.2s ease;
                }
                
                .child-selector:hover {
                    background: #f3f4f6;
                }
                
                .child-selector.selected {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    color: white;
                }
                
                .tab-button {
                    transition: all 0.2s ease;
                    border-radius: 8px 8px 0 0;
                }
                
                .tab-button.active {
                    background: #f0fdf4;
                    border-bottom: 3px solid #059669;
                    color: #059669;
                }
            `}</style>

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
                {/* Header */}
                <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center space-x-4">
                                <div>
                                    <BookOpen className="w-12 h-12 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        {selectedChild ? `${selectedChild.name}'s Activities` : "Learner's Activities"}
                                    </h1>
                                    <p className="text-green-100">
                                        Track assignments, assessments and learning progress
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || selectedChild?.id || ''}
                                        onChange={(e) => {
                                            const child = children.find(c => (c.student_id || c.id) === e.target.value);
                                            if (child) handleChildSelect(child);
                                        }}
                                        className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 appearance-none cursor-pointer pr-10"
                                        disabled={!children.length}
                                    >
                                        {children.length === 0 ? (
                                            <option value="" className="text-gray-900">No children linked</option>
                                        ) : (
                                            children.map(child => (
                                                <option key={child.student_id || child.id} value={child.student_id || child.id} className="text-gray-900">
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown className="w-4 h-4 text-white absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="max-w-7xl mx-auto px-6 py-8">
                    {!selectedChild ? (
                        <div className="text-center py-12">
                            <div className="bg-white rounded-3xl shadow-xl p-12 card-parent">
                                <BookOpen className="w-20 h-20 mx-auto mb-6 text-green-500" />
                                <h2 className="text-3xl font-bold gradient-text mb-4">Select a Learner</h2>
                                <p className="text-gray-600 text-lg mb-6">
                                    {children.length === 0
                                        ? "No children are linked to your account. Please contact your school administrator."
                                        : "Choose a child from the dropdown above to view their learning activities and progress."
                                    }
                                </p>
                                {children.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                                        {children.map((child) => (
                                            <div
                                                key={child.student_id || child.id}
                                                onClick={() => handleChildSelect(child)}
                                                className="child-selector p-6 rounded-2xl cursor-pointer"
                                            >
                                                <div className="text-center">
                                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
                                                        <span className="text-white font-bold text-xl">
                                                            {child.name.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <h3 className="font-bold text-gray-900 text-lg">{child.name}</h3>
                                                    <p className="text-gray-600">Grade {child.grade || 'N/A'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Child Info Card */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                            <span className="text-white font-bold text-xl">
                                                {selectedChild.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold gradient-text">{selectedChild.name}</h2>
                                            <p className="text-gray-600 font-medium">
                                                Grade {selectedChild.grade || 'N/A'} • {selectedChild.school || 'School'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-green-600">{assignments.length}</div>
                                            <div className="text-sm text-gray-600">Total Activities</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-blue-600">
                                                {assignments.filter(a => a.completed).length}
                                            </div>
                                            <div className="text-sm text-gray-600">Completed</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-orange-600">
                                                {assignments.filter(a => !a.completed && (!a.due_date || new Date(a.due_date) >= new Date())).length}
                                            </div>
                                            <div className="text-sm text-gray-600">Pending</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tab Navigation */}
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden card-parent">
                                <div className="flex border-b border-gray-100">
                                    {[
                                        { id: 'all', name: 'All Activities', count: assignments.length, icon: BookOpen, color: 'green' },
                                        { id: 'pending', name: 'Pending', count: assignments.filter(a => !a.completed && (!a.due_date || new Date(a.due_date) >= new Date())).length, icon: Clock, color: 'orange' },
                                        { id: 'completed', name: 'Completed', count: assignments.filter(a => a.completed).length, icon: CheckCircle, color: 'green' },
                                        { id: 'overdue', name: 'Overdue', count: assignments.filter(a => !a.completed && a.due_date && new Date(a.due_date) < new Date()).length, icon: AlertCircle, color: 'red' }
                                    ].map((tab) => {
                                        const Icon = tab.icon;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`tab-button flex-1 px-6 py-4 flex items-center justify-center space-x-2 font-medium transition-all ${activeTab === tab.id ? 'active text-green-600' : 'text-gray-500 hover:text-gray-700'
                                                    }`}
                                            >
                                                <Icon className="w-5 h-5" />
                                                <span>{tab.name}</span>
                                                <span className={`px-2 py-1 text-xs rounded-full ${activeTab === tab.id
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {tab.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Activities Content */}
                                <div className="p-6">
                                    {loading ? (
                                        <div className="text-center py-12">
                                            <Loader className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
                                            <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Activities</h3>
                                            <p className="text-gray-600">Please wait while we fetch the latest information...</p>
                                        </div>
                                    ) : getFilteredAssignments().length > 0 ? (
                                        <div className="grid grid-cols-1 gap-6">
                                            {getFilteredAssignments().map((assignment, index) => {
                                                const isCompleted = assignment.completed;
                                                const isOverdue = !assignment.completed && assignment.due_date && new Date(assignment.due_date) < new Date();
                                                const statusClass = isCompleted ? 'status-completed' : isOverdue ? 'status-overdue' : 'status-pending';

                                                return (
                                                    <div
                                                        key={assignment.assignment_id || assignment.id || index}
                                                        className="activity-card p-6 cursor-pointer"
                                                        onClick={() => handleAssignmentSelect(assignment)}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex items-start space-x-4 flex-1">
                                                                <div className="flex-shrink-0">
                                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${statusClass}`}>
                                                                        {getStatusIcon(assignment)}
                                                                    </div>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-start justify-between mb-2">
                                                                        <div>
                                                                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                                                                                {assignment.title || assignment.domain || 'Assignment'}
                                                                            </h3>
                                                                            <p className="text-sm font-medium text-gray-600 mb-2">
                                                                                {assignment.domain || assignment.subject || 'Subject'}
                                                                            </p>
                                                                        </div>
                                                                        <span className={`px-4 py-2 text-sm font-bold rounded-full ${statusClass}`}>
                                                                            {getStatusText(assignment)}
                                                                        </span>
                                                                    </div>

                                                                    {assignment.description && (
                                                                        <p className="text-gray-700 mb-4 line-clamp-2">
                                                                            {assignment.description}
                                                                        </p>
                                                                    )}

                                                                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                                                                        {assignment.created_at && (
                                                                            <div className="flex items-center space-x-1">
                                                                                <Calendar className="w-4 h-4" />
                                                                                <span>Created: {formatDate(assignment.created_at)}</span>
                                                                            </div>
                                                                        )}
                                                                        {assignment.due_date && (
                                                                            <div className="flex items-center space-x-1">
                                                                                <Clock className="w-4 h-4" />
                                                                                <span>Due: {formatDate(assignment.due_date)}</span>
                                                                            </div>
                                                                        )}
                                                                        {assignment.score !== undefined && (
                                                                            <div className="flex items-center space-x-1">
                                                                                <Star className="w-4 h-4" />
                                                                                <span>Score: {assignment.score}%</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <ChevronRight className="w-6 h-6 text-gray-400 ml-4 flex-shrink-0" />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-16">
                                            <div className="mb-6">
                                                <BookOpen className="w-20 h-20 text-gray-300 mx-auto" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                                No {activeTab === 'all' ? 'Activities' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + ' Activities'} Found
                                            </h3>
                                            <p className="text-gray-600 text-lg">
                                                {selectedChild?.name} has no {activeTab === 'all' ? '' : activeTab + ' '}activities at the moment.
                                            </p>
                                            <div className="mt-8">
                                                <button
                                                    onClick={() => setActiveTab('all')}
                                                    className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105"
                                                >
                                                    View All Activities
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Notification
                notification={notification}
                setNotification={setNotification}
            />
        </Layout>
    );
};

export default LearnerActivities;